import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search,
  Users,
  BedDouble,
  Plus,
  CreditCard,
  Wallet,
  AlignJustify,
  FlaskConical,
  Pill,
  Image as ImageIcon,
  FileText,
  Settings,
  Lock,
  Unlock,
  Undo2,
  PlusCircle,
  ChevronLeft,
  User,
  ShieldCheck,
} from 'lucide-react';

const iconClasses =
  'w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all duration-200';

const Sidebar = ({ menuItems, activeSection, setActiveSection }) => {
  const role = localStorage.getItem('userRole') || 'staff';

  const navIcons = useMemo(() => {
    const allIcons = [
      { key: 'search', Icon: Search, label: 'Search', sectionKey: null },
      { key: 'patient-portal', Icon: User, label: 'Patient Portal', sectionKey: 'patient-portal', roles: ['patient'] },
      { key: 'patient-admin', Icon: Users, label: 'Patient Administration', sectionKey: 'patient-admin', roles: ['staff', 'admin', 'receptionist', 'doctor', 'nurse'] },
      { key: 'inpatient', Icon: BedDouble, label: 'Inpatient', sectionKey: 'inpatient', roles: ['staff', 'admin', 'doctor', 'nurse'] },
      { key: 'ehr', Icon: Plus, label: 'EHR', sectionKey: 'ehr', roles: ['staff', 'admin', 'doctor', 'nurse'] },
      { key: 'billing', Icon: CreditCard, label: 'Billing & Insurance', sectionKey: 'billing-insurance', roles: ['staff', 'admin'] },
      { key: 'accounting', Icon: Wallet, label: 'Accounting', sectionKey: 'accounting', roles: ['staff', 'admin'] },
      { key: 'inventory', Icon: AlignJustify, label: 'Inventory & Products', sectionKey: 'inventory-products', roles: ['staff', 'admin'] },
      { key: 'laboratory', Icon: FlaskConical, label: 'Laboratory', sectionKey: 'laboratory', roles: ['staff', 'admin', 'doctor', 'nurse'] },
      { key: 'pharmacy', Icon: Pill, label: 'Pharmacy', sectionKey: 'pharmacy', roles: ['staff', 'admin', 'doctor', 'nurse'] },
      { key: 'imaging', Icon: ImageIcon, label: 'Imaging', sectionKey: 'imaging', roles: ['staff', 'admin', 'doctor', 'nurse'] },
      { key: 'analytics', Icon: FileText, label: 'Analytics', sectionKey: 'analytics', roles: ['staff', 'admin', 'receptionist', 'doctor', 'nurse'] },
      { key: 'configuration', Icon: Settings, label: 'Configuration', sectionKey: 'configuration', roles: ['staff', 'admin', 'receptionist', 'doctor', 'nurse'] },
      { key: 'admin', Icon: ShieldCheck, label: 'Administration', sectionKey: 'admin', roles: ['admin'] },
    ];

    return allIcons.filter(icon => !icon.roles || icon.roles.includes(role));
  }, [role]);

  const [isLocked, setIsLocked] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleLockToggle = () => {
    setIsLocked((prev) => {
      const next = !prev;
      if (next) {
        setIsCollapsed(false);
      } else {
        setIsCollapsed(true);
      }
      return next;
    });
  };

  const handleCollapseToggle = () => {
    if (isLocked) return;
    setIsCollapsed((prev) => !prev);
  };

  const handleRailToggle = () => {
    if (isLocked) return;
    setIsCollapsed((prev) => !prev);
  };

  const handleSectionClick = (sectionKey) => {
    if (sectionKey) {
      setActiveSection(sectionKey);
      if (isCollapsed) {
        setIsCollapsed(false);
      }
    } else if (!isLocked) {
      setIsCollapsed(false);
    }
  };

  const resolvedActiveSection = menuItems[activeSection] ? activeSection : Object.keys(menuItems)[0] || null;
  const activeSectionConfig = resolvedActiveSection ? menuItems[resolvedActiveSection] : null;

    const location = useLocation();
  
    return (
      <aside
        className={`flex h-screen bg-slate-50 border-r border-slate-200 flex-none transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-[360px]'
        }`}
      >
        {/* Icon Rail */}
        <div className="w-16 bg-slate-900 text-white flex flex-col items-center py-5 space-y-2.5">
          <button
            type="button"
            onClick={handleRailToggle}
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all ${
              isLocked ? 'cursor-not-allowed opacity-40' : 'hover:scale-110 active:scale-95'
            }`}
            disabled={isLocked}
            aria-label="Toggle sidebar width"
          >
            <ChevronLeft
              className={`w-5 h-5 transition-transform duration-500 ${isCollapsed ? 'rotate-180 text-white/80' : ''}`}
              strokeWidth={1.5}
            />
          </button>
          {navIcons.map(({ key, Icon, label, sectionKey }) => {
            const isActive = sectionKey && resolvedActiveSection === sectionKey;
            const buttonStyles = isActive
              ? 'bg-white text-indigo-600 shadow-xl shadow-indigo-500/30 active:scale-95'
              : 'text-slate-400 hover:text-white hover:bg-slate-800 hover:scale-105 active:scale-90';
  
            return (
              <button
                key={key}
                title={label}
                onClick={() => handleSectionClick(sectionKey)}
                className={`${iconClasses} ${buttonStyles}`}
              >
                <Icon className={`w-4.5 h-4.5 ${isActive ? 'text-indigo-600' : 'text-current'}`} strokeWidth={isActive ? 2 : 1.5} />
              </button>
            );
          })}
          <div className="mt-auto w-10 h-10 rounded-full bg-white flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-all shadow-lg hover:shadow-indigo-500/20">
            <PlusCircle className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
          </div>
        </div>
  
        {/* Content Panel */}
        {!isCollapsed && (
          <div className="flex-1 w-[300px] bg-slate-100/60 px-4 py-5 overflow-y-auto text-[13px]">
            <div className="flex items-center justify-between text-slate-500 border-b border-slate-200 pb-2.5 mb-4">
              <div className="font-semibold tracking-wide text-slate-600 text-sm">
                {activeSectionConfig?.title || 'Menu'}
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <button
                  type="button"
                  onClick={handleLockToggle}
                  className={`p-1 rounded-full transition-colors ${
                    isLocked ? 'text-slate-600' : 'hover:bg-slate-200'
                  }`}
                  aria-pressed={isLocked}
                  aria-label={isLocked ? 'Unlock sidebar' : 'Lock sidebar'}
                >
                  {isLocked ? <Lock className="w-5 h-5" strokeWidth={1.8} /> : <Unlock className="w-5 h-5" strokeWidth={1.8} />}
                </button>
                <button
                  type="button"
                  onClick={handleCollapseToggle}
                  className={`p-1 rounded-full transition-colors ${
                    isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-200'
                  }`}
                  aria-label="Toggle sidebar visibility"
                  disabled={isLocked}
                >
                  <Undo2 className="w-5 h-5" strokeWidth={1.8} />
                </button>
              </div>
            </div>
  
            {activeSectionConfig ? (
              <div className="mb-5">
                <h3 className="text-sm font-semibold tracking-wide text-indigo-600">
                  {activeSectionConfig.title}
                </h3>
                <div className="mt-3 space-y-3 pl-2 border-l-2 border-slate-200">
                  {Object.keys(activeSectionConfig.items).map((itemKey) => {
                    const item = activeSectionConfig.items[itemKey];
                    return (
                      <div key={itemKey} className="pl-4">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{item.title}</p>
                        <ul className="space-y-1 text-[12px]">
                          {item.subItems.map((subItem) => {
                            const slug = subItem.toLowerCase().replace(/\s+/g, '-');
                            const targetPath = `/dashboard/${resolvedActiveSection}/${itemKey}/${slug}`;
                            const isActiveSubItem = location.pathname === targetPath;
                            
                            return (
                              <li key={subItem}>
                                <Link
                                  className={`block py-1.5 px-3 rounded-lg transition-all duration-200 ${
                                    isActiveSubItem 
                                      ? 'bg-indigo-600/10 text-indigo-700 font-bold shadow-sm' 
                                      : 'text-slate-700 hover:text-indigo-600'
                                  }`}
                                  to={targetPath}
                                  onClick={() => {
                                    if (!isLocked) setIsCollapsed(true);
                                  }}
                                >
                                  {subItem}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Select a section to view its shortcuts.</div>
            )}
          </div>
        )}
      </aside>
    );
};

export default Sidebar;
